import { NumberFormatter, NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { useTranslation } from "react-i18next";
import Tag, { type TagVariant } from "~/common/components/Tag/Tag";
import { styled, Box } from "~/common/mui";
import { C_IN_STOCK_AURA } from "~/common/components/icon/Colors";

interface StockIndicatorProps {
  className?: string;
  level: StockLevel;
  changeText?: string;
  checkAvailability?: boolean;
}

export const StockIndicatorIcon = styled("div")<JSX.IntrinsicElements["div"] & { level: StockLevel; size?: number }>(({
  theme,
  level,
  size = 12,
}) => {
  let background;
  let border;
  let iconSize = size;
  let padding;
  switch (level) {
    case StockLevel.IN_STOCK:
      background = theme.palette.primary.main;
      border = `2px solid ${C_IN_STOCK_AURA}`;
      iconSize = 12;
      padding = 0;
      break;
    case StockLevel.PARTIAL_STOCK:
      background = `linear-gradient(to top, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.text.secondary} 51%, ${theme.palette.text.secondary} 100%)`;
      iconSize = 8;
      padding = 0;
      break;
    case StockLevel.NOT_IN_STOCK:
      background = theme.palette.text.secondary;
      iconSize = 8;
      padding = 0;
      break;
  }

  const dimension = "var(--size)";

  return {
    "::after": {
      content: '""',
      display: "block",
      width: "100%",
      height: "100%",
      background,
      border,
      borderRadius: "50%",
    },
    "--size": `${iconSize}px`,
    display: "inline-block",
    width: dimension,
    height: dimension,
    borderRadius: "50%",
    flex: "none",
    padding: `${padding}px`,
  };
});

export enum StockLevel {
  IN_STOCK,
  PARTIAL_STOCK,
  NOT_IN_STOCK,
}

export const stockConfig = {
  [StockLevel.NOT_IN_STOCK]: {
    translationKey: "stockIndicator.missing",
    variant: "default",
  },
  [StockLevel.PARTIAL_STOCK]: {
    translationKey: "stockIndicator.partial",
    variant: "primary",
  },
  [StockLevel.IN_STOCK]: {
    translationKey: "stockIndicator.exists",
    variant: "primary",
  },
};

const Root = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  alignItems: "center",
}));

const Text = styled("span")(({ theme }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.text.primary,
}));

const CheckAvailability = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
  marginLeft: theme.spacing(0.625), // 5px
  "&:hover": {
    textDecoration: "underline",
  },
}));

export const StockIndicator = (props: StockIndicatorProps) => {
  const { level, className, changeText } = props;
  const { t } = useTranslation();

  const { translationKey } = stockConfig[level];

  return (
    <Root className={className}>
      <StockIndicatorIcon level={level} />
      <Text data-test-id={"stock-indicator"}>{changeText ? changeText : t(translationKey)}</Text>
      {props.checkAvailability && <CheckAvailability>{t("stockIndicator.check.availability")}</CheckAvailability>}
    </Root>
  );
};

interface StockIndicatorV2Props {
  className?: string;
  stock: number;
}

interface StockIndicatorV2PropsAlternative {
  className?: string;
  level: StockLevel;
  changeText?: string;
}

export const StockIndicatorV2 = (props: StockIndicatorV2Props | StockIndicatorV2PropsAlternative) => {
  const { t } = useTranslation();

  let variant: TagVariant;
  let level;
  let label;
  if ("stock" in props) {
    if (props.stock > 0) {
      variant = "primary";
      level = StockLevel.IN_STOCK;
      label = NF.getDefaultFormatter().format(props.stock) + " " + t(stockConfig[level].translationKey);
    } else {
      variant = "default";
      level = StockLevel.NOT_IN_STOCK;
      label = t(stockConfig[level].translationKey);
    }
  } else {
    level = props.level;
    variant = stockConfig[level].variant as TagVariant;
    label = props.changeText || t(stockConfig[level].translationKey);
  }

  // Todo - tag should have a startIcon and endIcon just like button
  return (
    <Tag variant={variant} className={props.className}>
      <StockIndicatorIcon level={level} />
      {label}
    </Tag>
  );
};

StockIndicator.V2 = styled(StockIndicatorV2)(({ theme }) => ({
  display: "inline-flex",
  gap: theme.spacing(0.5),
}));

interface SimpleStockIndicatorProps {
  className?: string;
  inStock?: boolean;
  checkAvailability?: boolean;
}

export const SimpleStockIndicator = ({ className, inStock, checkAvailability }: SimpleStockIndicatorProps) => (
  <StockIndicator
    className={className}
    level={inStock ? StockLevel.IN_STOCK : StockLevel.NOT_IN_STOCK}
    checkAvailability={checkAvailability}
  />
);

interface QuantityStockIndicatorProps {
  className?: string;
  quantity: number;
}

export const QuantityStockIndicator = ({ className, quantity }: QuantityStockIndicatorProps) => {
  const level = quantity > 0 ? StockLevel.IN_STOCK : StockLevel.NOT_IN_STOCK;

  const { t } = useTranslation();
  const label =
    quantity > 0
      ? t("stockIndicator.existsWithQty", { amount: NumberFormatter.getFormatter().format(quantity) })
      : t("stockIndicator.missing");

  return <StockIndicator className={className} level={level} changeText={label} />;
};

export function getStockLevel(availableStock: number = 0, requestedQty: number = 0) {
  if (!availableStock) {
    return StockLevel.NOT_IN_STOCK;
  }
  if (availableStock >= requestedQty) {
    return StockLevel.IN_STOCK;
  } else {
    return StockLevel.PARTIAL_STOCK;
  }
}

export const BackInStockIndicator = () => {
  const { t } = useTranslation();

  return <Tag variant={"backInStock"}>{t("recentlyViewProducts.backInStock")}</Tag>;
};

export default StockIndicator;
